import React, {useState, useEffect} from 'react';
import {useTranslation} from '@deviceinsight/ng-ui-scale-lib/api';
import {Link} from 'react-router-dom';
import {AssetGroup, Pageable, PaginationSettings} from '@deviceinsight/ng-ui-api-client';
import {ContentHeader, Reactable} from '@deviceinsight/ng-ui-components';
import {SpinnerContainer} from '@deviceinsight/ng-ui-basic-components';
import type {GlobalJob} from './types';
import './types';
import {CREATE_GLOBAL_JOB} from './consts';
import globalJobColumnDefs from './columnDefs/globalJobColumnDefs';
import {fetchGlobalJobs} from '../../api/updateJobsApi';
import './AdminList.less';
import hasAuthority from '../common/hasAuthority';

type Props = {
	assetGroup: AssetGroup;
};

const HEADER_TITLE = {
	id: 'globaljobs.list.title',
	defaultValue: 'Global Jobs'
};
const INITIAL_PAGINATION_SETTINGS: PaginationSettings = {
	page: 0,
	size: 15,
	sort: {
		property: 'createdAt',
		direction: 'DESC'
	}
};

const GlobalList: React.FC<Props> = ({assetGroup}) => {
	const {t} = useTranslation();
	const [jobs, setJobs] = useState<Pageable<GlobalJob> | null | undefined>();
	const [loading, setLoading] = useState<boolean>(false);
	const [paginationSettings, setPaginationSettings] = useState<PaginationSettings>(INITIAL_PAGINATION_SETTINGS);
	const canCreateJob = hasAuthority(CREATE_GLOBAL_JOB);
	const assetGroupId = assetGroup.id;
	useEffect(() => {
		setLoading(true);
		fetchGlobalJobs(paginationSettings)
			.then((data) => {
				setJobs(data);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [paginationSettings]);

	const createGlobalJob = t('globaljobs.list.create', {
		defaultValue: 'Create global job'
	});

	return (
		<div className="admin-list">
			<ContentHeader title={HEADER_TITLE}>
				<div className="di header-toolbar">
					{canCreateJob && (
						<Link
							className="tool add"
							to={`/assetgroups/${assetGroupId}/globaljobs/create`}
							title={createGlobalJob}
						/>
					)}
				</div>
			</ContentHeader>
			<SpinnerContainer show={loading}>
				<div
					className="bundle-version-table"
					style={{
						minHeight: 50
					}}>
					<Reactable<GlobalJob>
						isLoading={false}
						uniqueItemKey={(version) => version.id}
						columnDefs={globalJobColumnDefs(assetGroup.id?.toString())}
						paginationSettings={paginationSettings}
						onChangePage={(page) => setPaginationSettings({...paginationSettings, page})}
						onSort={(property, direction) =>
							setPaginationSettings({
								...paginationSettings,
								sort: {
									property,
									direction
								}
							})
						}
						pageable={jobs}
					/>
				</div>
			</SpinnerContainer>
		</div>
	);
};

export default GlobalList;
